// The LOTO card on the QEW Dashboard, and the QEW's own LOTO report.
// Words: QEW_DASHBOARD_WORDS.md, LOTO section (approved 2026-10-06).
// Mockup: MOCKUP_QEW_DASHBOARD_APPROVED.html screens 1 to 5.
// Shared by the Dashboard and the reportLotoDate server step, and unit tested.
// Nothing here reads or writes the database.
//
// Rules:
// * The QEW reports. Someone else verifies. Only a verified date counts toward Active.
// * 1-year reminder, 3-year expiry (NFPA 70E). Amber and red are notices. Nothing is blocked.
// * A new report on top of a verified date keeps the verified date as lotoLastVerified.
//   It still counts toward Active. A later expiry never takes Active away.

import { addYears, lotoCheck, LOTO_REMINDER_YEARS, LOTO_VALID_YEARS } from "./claim";
import { daysBetween } from "./qew-training";

/** Days before the 3-year date when the card turns amber and the reminder email goes. */
export const LOTO_WARNING_DAYS = 30;

/** The person fields the LOTO card reads. */
export interface LotoPerson {
  lotoTrainedOn: string | null;
  lotoExpiresOn: string | null;
  lotoReportedOn: string | null;
  lotoNoneReported: boolean;
  lotoVerifiedOn: string | null;
  lotoVerifiedByName: string | null;
  lotoSource: string | null;
  /** The last verified date, kept when the QEW reports a new one. */
  lotoLastVerifiedOn: string | null;
}

export type LotoState = "notOnFile" | "noneReported" | "pending" | "current" | "overOneYear" | "expiringSoon" | "expired";

export interface LotoCard {
  state: LotoState;
  tone: "green" | "amber" | "red" | "grey";
  title: string;
  line: string;
  /** The gold Report LOTO Date button shows under the card. */
  askToReport: boolean;
}

/** 2026-05-05 reads 05/05/2026, as on the Training card. */
export function usDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

/** True once any LOTO date has been verified by someone else, now or before. */
export function lotoEverVerified(p: Pick<LotoPerson, "lotoVerifiedOn" | "lotoLastVerifiedOn">): boolean {
  return Boolean(p.lotoVerifiedOn || p.lotoLastVerifiedOn);
}

/** The LOTO card for a person, against today in site time. */
export function lotoCardOf(p: LotoPerson, qewActive: boolean, today: string): LotoCard {
  const date = p.lotoTrainedOn;
  if (!date) {
    if (p.lotoNoneReported) {
      return {
        state: "noneReported",
        tone: "grey",
        title: "Not on file",
        line: qewActive ? "You reported no LOTO authorization." : "QEW status waits on a verified LOTO date.",
        askToReport: true,
      };
    }
    return {
      state: "notOnFile",
      tone: "grey",
      title: "Not on file",
      line: qewActive ? "No LOTO date reported." : "QEW status waits on a verified LOTO date.",
      askToReport: true,
    };
  }

  const expiresOn = p.lotoExpiresOn || addYears(date, LOTO_VALID_YEARS);
  if (expiresOn < today) {
    return { state: "expired", tone: "red", title: "LOTO expired", line: `Expired ${usDate(expiresOn)} · report your new date`, askToReport: true };
  }

  if (!p.lotoVerifiedOn) {
    const fromSheet = p.lotoSource === "Sheet copy";
    return {
      state: "pending",
      tone: "amber",
      title: "Pending verification",
      line: `${fromSheet ? "Carried from the Sheet" : "You reported"} ${usDate(date)}. Your manager, site manager or Safety confirms it.`,
      askToReport: false,
    };
  }

  const left = daysBetween(today, expiresOn);
  if (left <= LOTO_WARNING_DAYS) {
    return {
      state: "expiringSoon",
      tone: "amber",
      title: `LOTO expires in ${left} ${left === 1 ? "day" : "days"}`,
      line: `${usDate(expiresOn)} · report your new date when you have it`,
      askToReport: false,
    };
  }
  if (addYears(date, LOTO_REMINDER_YEARS) < today) {
    return {
      state: "overOneYear",
      tone: "amber",
      title: "Over 1 year since your LOTO refresher",
      line: `LOTO date ${usDate(date)} · still current through ${usDate(expiresOn)}`,
      askToReport: false,
    };
  }
  const by = p.lotoVerifiedByName ? ` by ${p.lotoVerifiedByName}` : "";
  return {
    state: "current",
    tone: "green",
    title: `Current through ${usDate(expiresOn)}`,
    line: `LOTO date ${usDate(date)} · Verified${by} ${usDate(p.lotoVerifiedOn)}`,
    askToReport: false,
  };
}

/** Why a LOTO report is refused before the database is touched. Null when it can go. */
export function lotoReportProblem(lotoDate: string, noneReported: boolean, today: string): string | null {
  const c = lotoCheck(lotoDate, noneReported, today);
  if (c.kind === "empty") return "Enter your LOTO authorization date, or tick the box.";
  if (c.kind === "future") return "That LOTO date is in the future. Check it before sending.";
  if (c.kind === "expired") return "That date is over 3 years old. Enter your most recent LOTO date.";
  return null;
}

/**
 * The person fields a Dashboard report writes. Stamped with the badge, the holder
 * name, and how the name was confirmed. Verification is cleared: the new date waits.
 * A verified date on file is kept as lotoLastVerified so Active is never lost.
 */
export function lotoReportFields(
  current: { lotoTrainedOn: string | null; lotoExpiresOn: string | null; lotoVerifiedOn: string | null; lotoVerifiedBy: string | null; lotoVerifiedByName: string | null },
  report: { lotoDate: string; noneReported: boolean },
  who: { badgeId: string; holderName: string },
  today: string,
  nowIso: string,
): Record<string, unknown> {
  const none = report.noneReported;
  const fields: Record<string, unknown> = {
    lotoTrainedOn: none ? null : report.lotoDate,
    lotoExpiresOn: none ? null : addYears(report.lotoDate, LOTO_VALID_YEARS),
    lotoNoneReported: none,
    lotoReportedOn: today,
    lotoReportedAt: nowIso,
    lotoReportedBy: who.badgeId,
    lotoReportedByName: who.holderName,
    lotoReportedHow: "QEW Dashboard, name confirmed",
    lotoSource: "QEW Dashboard",
    lotoVerifiedBy: null,
    lotoVerifiedByName: null,
    lotoVerifiedOn: null,
  };
  if (current.lotoVerifiedOn) {
    fields.lotoLastVerified = {
      trainedOn: current.lotoTrainedOn,
      expiresOn: current.lotoExpiresOn,
      verifiedBy: current.lotoVerifiedBy,
      verifiedByName: current.lotoVerifiedByName,
      verifiedOn: current.lotoVerifiedOn,
    };
  }
  return fields;
}
